import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, update, set, push } from "firebase/database";
import { onAuthStateChanged, sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import SettingsUserGroups from "../SettingsUserGroups";

function SettingsPage() {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [preferedCat, setPreferedCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [supportForm, setSupportForm] = useState({
    SUPPORT_REASON: "",
    SUPPORT_TITLE: "",
    SUPPORT_DESCRIPTION: ""
  });
  const [supportTickets, setSupportTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketMessages, setTicketMessages] = useState({});
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      const database = getDatabase();
      const userRef = ref(database, "USERS/" + user.uid);

      get(userRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            setUserData(data);
            setDisplayName(data.USERDISPLAYNAME || "");
            setPreferedCat(data.USERPREFEREDCATEGORY || "");
          }
        })
        .catch((error) => {
          console.error("Failed to read user data:", error);
        })
        .finally(() => setLoading(false));

      // Fetch support tickets
      fetchSupportTickets(user.uid);
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchSupportTickets = async (userId) => {
    try {
      const database = getDatabase();
      const supportRef = ref(database, `USERS/${userId}/SUPPORT`);
      
      const snapshot = await get(supportRef);
      if (snapshot.exists()) {
        const ticketsData = snapshot.val();
        const ticketsArray = Object.values(ticketsData).sort((a, b) => {
          // Sort by most recent first
          return new Date(b.SUPPORT_DATE) - new Date(a.SUPPORT_DATE);
        });
        setSupportTickets(ticketsArray);
      } else {
        setSupportTickets([]);
      }
    } catch (error) {
      setSupportTickets([]);
    }
  };

  const fetchTicketMessages = async (userId, ticketId) => {
    try {
      const database = getDatabase();
      const messagesRef = ref(database, `USERS/${userId}/SUPPORT/${ticketId}/MESSAGES`);
      
      const snapshot = await get(messagesRef);
      if (snapshot.exists()) {
        const messagesData = snapshot.val();
        const messagesArray = Object.entries(messagesData).map(([id, msg]) => ({
          id,
          ...msg
        })).sort((a, b) => a.createdAt - b.createdAt);
        
        setTicketMessages(prev => ({
          ...prev,
          [ticketId]: messagesArray
        }));
      } else {
        setTicketMessages(prev => ({
          ...prev,
          [ticketId]: []
        }));
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      setTicketMessages(prev => ({
        ...prev,
        [ticketId]: []
      }));
    }
  };

  const handleSendMessage = async (ticketId) => {
    if (!messageText.trim()) return;

    const user = auth.currentUser;
    if (!user) return;

    setSendingMessage(true);
    try {
      const database = getDatabase();
      const messageRef = ref(database, `USERS/${user.uid}/SUPPORT/${ticketId}/MESSAGES`);
      
      const now = new Date();
      const newMessage = {
        MESSAGE_TEXT: messageText,
        SENT_BY: user.uid,
        SENT_DATE: now.toISOString(),
        createdAt: Date.now()
      };

      await push(messageRef, newMessage);
      setMessageText("");
      
      // Refresh messages
      await fetchTicketMessages(user.uid, ticketId);
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const updateDisplayName = async () => {
    if (!displayName.trim()) {
      // alert("Display name cannot be empty");
      return;
    }


    setSaving(true);

    try {
      const database = getDatabase();
      const user = auth.currentUser;

      await update(ref(database, "USERS/" + user.uid), {
        USERDISPLAYNAME: displayName,
        USERPREFEREDCATEGORY: preferedCat,
      });

      setUserData((prev) => ({
        ...prev,
        USERDISPLAYNAME: displayName, 
        USERPREFEREDCATEGORY: preferedCat,
      }));

      // alert("Display name updated!");
    } catch (error) {
      console.error(error);
      // alert("Failed to update display name");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    const user = auth.currentUser;
    if (!user?.email) return;

    try {
      await sendPasswordResetEmail(auth, user.email);
      // alert("Password reset email sent!");
    } catch (error) {
      console.error(error);
      // alert("Failed to send password reset email");
    }
  };

  const supportReasons = [
    "Technical Issue",
    "Account Issue",
    "Event Listing Issue",
    "Feature Request",
    "Bug",
    "General Issue",
    "Other"
  ];

  const handleSupportChange = (e) => {
    const { name, value } = e.target;
    setSupportForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();

    if (!supportForm.SUPPORT_REASON || !supportForm.SUPPORT_DESCRIPTION) {
      // console.log("Please fill in all fields");
      return;
    }

    // setSubmittingSupport(true);

    try {
      const user = auth.currentUser;

      const database = getDatabase();

      if (!user) {
        // console.log("You must be logged in");
        return;
      }

      var supportUID = "SUPPORTUID_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
   
      
      const supportData = {
        SUPPORTID: supportUID,
        SUPPORT_REASON: supportForm.SUPPORT_REASON,
        SUPPORT_TITLE: supportForm.SUPPORT_TITLE,
        SUPPORT_DESCRIPTION: supportForm.SUPPORT_DESCRIPTION,
        SUPPORT_DATE: new Date().toISOString().split('T')[0],
        SUPPORT_USER: user.uid,
        SUPPORT_ISOPEN: "Y",
        createdAt: Date.now()
      };

        const supportPath = `USERS/${user.uid}/SUPPORT/${supportData.SUPPORTID}`;
        await set(ref(database, supportPath), supportData);
      
      // console.log("Support ticket created successfully!");
      setSupportForm({ SUPPORT_REASON: "", SUPPORT_DESCRIPTION: "" });
      
      // Refresh support tickets list
      await fetchSupportTickets(user.uid);
      
      // Close modal
      const modal = document.getElementById("supportmodal");
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      if (bootstrapModal) bootstrapModal.hide();
    } catch (error) {
      // console.error("Error submitting support ticket:", error);
      // console.log("Failed to create support ticket");
    } finally {
      // setSubmittingSupport(false);
        // console.log("Failed to create support ticket");
    }
  };

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div className="row justify-content-center my-5">
      <div className="col-md-12">

        <div className="row">
          <div className="col-md-6">
            <h3 className="mb-0">Hello, {userData?.USERNAME}</h3>
          </div>
        </div>

        <div className="row">
          <div className="col-auto">
            <p className="mb-2 text-gray-500">
              Last Logged in: {userData?.USERLASTLOGIN} -  Account Created: {userData?.USERACCOUNTCREATED}
            </p>
          </div>
        </div>
        
        <div className="row">
          <div className="col-auto">
              {userData?.USERISADMIN === "Y" && (
                <span className="event-badges me-2">
                  <i className="bi bi-shield-check me-1 admin-badge"></i>
                   Admin
                </span> 
              )}
              {(userData?.USERISADMIN === "Y" ||
                userData?.USERISVERYFIED === "Y") && (
                  <span className="event-badges">
                    <i className="bi bi-check2-circle me-1 verified-badge"></i>
                    Verified
                  </span>
              )}  
          </div>
        </div>

        <ul className="nav nav-tabs mt-5">
          <button className="nav-link active" id="nav-usersettings-tab" data-bs-toggle="tab" data-bs-target="#nav-usersettings" type="button" role="tab" aria-controls="nav-usersettings" aria-selected="true">User Settings</button>
          <button className="nav-link" id="nav-support-tab" data-bs-toggle="tab" data-bs-target="#nav-support" type="button" role="tab" aria-controls="nav-support" aria-selected="false" tabIndex="-1">Support</button>
          <button className="nav-link" id="nav-groups-tab" data-bs-toggle="tab" data-bs-target="#nav-groups" type="button" role="tab" aria-controls="nav-groups" aria-selected="false" tabIndex="-1">Groups</button>
        </ul>


        <div className="tab-content" id="nav-tabContent">
          <div className="tab-pane fade active show" id="nav-usersettings" role="tabpanel" aria-labelledby="nav-usersettings-tab">

                <div className="row mt-4">
                  <div className="col-md-6">
                    <h5 className="card-title mb-1">User Settings</h5>
                    <p className="card-text mb-4 text-gray-500">
                      Manage your user settings here.
                    </p>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label">Display Name</label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                      />
                      <p className="small mb-3 mt-0 text-gray-500"> This name is shown to others. </p>
                    </div>
                    
                    <div className="mb-4">
                      <label className="form-label">Preferred Category</label>
                      <select
                        className="form-select mb-2"
                        value={preferedCat} 
                        onChange={(e) => setPreferedCat(e.target.value)}
                      > 
                      <option value="">None</option>
                      <option value="AUTOMOTIVE">Automotive</option>
                      <option value="SPORTS">Sports</option>
                      <option value="MUSIC">Music</option>
                      </select>

                      <p className="small mb-3 mt-0 text-gray-500"> This automatically loads data from your preferred category (e.g Manage Events or Attending). </p>
                    </div>
                    

                    <div className="mb-4">
                      <label className="form-label">Email</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData?.USEREMAIL || ""}
                        disabled
                      />
                    </div>

                    <div className="text-end">
                      <button
                        className="btn btn-primary"
                        onClick={updateDisplayName}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Update"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="filter-section"></div>

                <div className="row justify-content-between my-4 align-items-center">
                  <div className="col-6">
                    <h5 className="card-title mb-1">Password reset</h5>
                    <p className="card-text text-gray-500">
                      Click to reset your password.
                    </p>
                  </div>

                  <div className="col-6 text-end">
                    <button
                      className="btn btn-danger"
                      onClick={resetPassword}
                    >
                      Reset Password
                    </button>
                  </div>
                </div>

          </div>

          <div className="tab-pane fade" id="nav-support" role="tabpanel" aria-labelledby="nav-support-tab">

              <div className="row mt-4 align-items-center">
                  <div className="col-md-6">
                    <h5 className="card-title mb-1">Support</h5>
                    <p className="card-text mb-0 text-gray-500">
                      Create Support tickets and view them here in your support inbox.
                    </p>
                  </div>

                  <div className="col-md-6 text-md-end text-sm-start">
                      <button className="btn btn-primary"  data-bs-toggle="modal" data-bs-target="#supportmodal">
                        Create Support Ticket
                      </button>
                  </div>
                </div>

                <div className="filter-section"></div>

                  <div className="row justify-content-between my-4 align-items-start">
                      <div className="mb-4 w-100">
                        <label className="form-label">Support Inbox</label>
                        {supportTickets.length === 0 ? (
                          <div className="card p-3">
                            <p className="mb-0 text-gray-500">You have no support tickets yet.</p>
                          </div>
                        ) : (
                  <div className="support-tickets-list">
                            {supportTickets.map((ticket, index) => {
                              const isExpanded = selectedTicketId === ticket.SUPPORTID;
                              return (
                                <div key={index} className="card mb-3 border-0 shadow-sm">
                                  <div className="card-body">
                                    {/* Ticket Header */}
                                    <div className="row align-items-center">
                                      <div className="col-md-8">
                                        <h6 className="card-title mb-2 fw-bold">
                                          {ticket.SUPPORT_TITLE || ticket.SUPPORT_REASON}
                                        </h6>
                                        <p className="mb-2 text-muted small">
                                          #{ticket.SUPPORTID} <span className="event-badges">{ticket.SUPPORT_REASON}</span> 
                                        </p>
                                        <p className="text-gray-500 mb-0 small">
                                          <strong>Request:</strong> {ticket.SUPPORT_DESCRIPTION}
                                        </p>
                                      </div>

                                      <div className="col-md-4 text-md-end text-sm-start pt-3 pt-md-0">
                                        <div className="d-flex justify-content-md-end justify-content-sm-start align-items-center gap-2">
                                          <span className={`event-badges ${ticket.SUPPORT_ISOPEN === "Y" ? "open" : "resolved"}`}>
                                            {ticket.SUPPORT_ISOPEN === "Y" ? <><i className="bi bi-check2-circle me-1 admin-badge"></i> Open</> : <><i className="bi bi-check2-circle me-1 verified-badge"></i> Resolved</>}
                                          </span>
                                          <button 
                                            className={`btn btn-sm ${isExpanded ? "btn-primary" : "btn-primary"}`}
                                            onClick={() => {
                                              if (isExpanded) {
                                                setSelectedTicketId(null);
                                              } else {
                                                const user = auth.currentUser;
                                                if (user) {
                                                  setSelectedTicketId(ticket.SUPPORTID);
                                                  fetchTicketMessages(user.uid, ticket.SUPPORTID);
                                                }
                                              }
                                            }}
                                          >
                                            {isExpanded ? "Hide" : "Show"}
                                          </button>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Chat Section - Collapsible */}
                                    {isExpanded && (
                                      <div className="mt-4 pt-3 border-top">
                                        {/* Messages Container */}
                                        <div className="messages-container mb-3 p-3" style={{ backgroundColor: "var(--chat-bg)", borderRadius: "8px", maxHeight: "300px", overflowY: "auto", minHeight: "150px", border: "var(--chat-border)" }}>
                                          {!ticketMessages[ticket.SUPPORTID] || ticketMessages[ticket.SUPPORTID].length === 0 ? (
                                            <p className="text-center mb-0 small">No messages yet. Start the conversation!</p>
                                          ) : (
                                            ticketMessages[ticket.SUPPORTID].map((msg, idx) => {
                                              const isCurrentUserMessage = msg.SENT_BY === auth.currentUser?.uid;
                                              const msgDate = msg.SENT_DATE ? new Date(msg.SENT_DATE) : null;
                                              const formattedDateTime = msgDate ? `${msgDate.toLocaleDateString()} ${msgDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Unknown';
                                              return (
                                                <div key={idx} className={`mb-2 ${isCurrentUserMessage ? "text-end" : "text-start"}`}>
                                                  <div style={{
                                                    display: "inline-block",
                                                    backgroundColor: isCurrentUserMessage ? "var(--chat-message-user-bg)" : "var(--chat-message-admin-bg)",
                                                    color: "var(--chat-message-text)",
                                                    padding: "10px 14px",
                                                    borderRadius: "12px",
                                                    maxWidth: "75%",
                                                    wordWrap: "break-word"
                                                  }}>
                                                    <p className="mb-1 small">{msg.MESSAGE_TEXT}</p>
                                                    <small style={{ opacity: 0.7 }}>
                                                      {msg.SENT_BY === "ADMIN" ? "Admin" : "You"} • {formattedDateTime}
                                                    </small>
                                                  </div>
                                                </div>
                                              );
                                            })
                                          )}
                                        </div>

                                        {/* Message Input - Only if ticket is open */}
                                        {ticket.SUPPORT_ISOPEN === "Y" ? (
                                          <div className="row g-2">
                                            <div className="col">
                                              <textarea
                                                className="form-control form-control-sm"
                                                rows="2"
                                                placeholder="Type your message..."
                                                value={messageText}
                                                onChange={(e) => setMessageText(e.target.value)}
                                                disabled={sendingMessage}
                                                style={{ borderRadius: "8px" }}
                                              />
                                            </div>
                                            <div className="col-auto d-flex align-items-end">
                                              <button 
                                                className="btn btn-sm btn-primary"
                                                onClick={() => handleSendMessage(ticket.SUPPORTID)}
                                                disabled={sendingMessage || !messageText.trim()}
                                                style={{ borderRadius: "8px" }}
                                              >
                                                {sendingMessage ? "Sending..." : "Send"}
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="alert alert-info mb-0 small">
                                            <i className="bi bi-info-circle me-2"></i>
                                            This ticket is resolved. No more messages can be sent.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            
                          </div>
                        )}

                        
                      </div>

                  </div>

          </div>

             <div className="tab-pane fade" id="nav-groups" role="tabpanel" aria-labelledby="nav-groups-tab">
                <div className="row mt-4">
                  <div className="col-md-12">
                    <SettingsUserGroups />
                  </div>
                </div>
             </div>
        </div>



      </div>

      {/* support modal */}
      <div className="modal fade" id="supportmodal" tabIndex="-1" aria-labelledby="supportmodalLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
              <div className="row justify-content-between align-items-center p-3">
                <h1 className="modal-title fs-5" id="supportmodalLabel">Create Support Ticket</h1>
                <button type="button" className="btn btn-close text-primary" data-bs-dismiss="modal" aria-label="Close"><i className="bi bi-x-lg"></i></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSupportSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Support Reason</label>
                    <select
                      name="SUPPORT_REASON"
                      className="form-select"
                      value={supportForm.SUPPORT_REASON}
                      onChange={handleSupportChange}
                      required
                    >
                      <option value="">Select a reason...</option>
                      {supportReasons.map(reason => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      name="SUPPORT_TITLE"
                      className="form-control"
                      value={supportForm.SUPPORT_TITLE}
                      onChange={handleSupportChange}
                      required
                    ></input>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      name="SUPPORT_DESCRIPTION"
                      className="form-control"
                      rows="5"
                      placeholder="Please describe your issue in detail..."
                      value={supportForm.SUPPORT_DESCRIPTION}
                      onChange={handleSupportChange}
                      required
                    ></textarea>
                  </div>
                </form>
              </div>
              <div className="row justify-content-between align-items-center p-4">
                  <div className="col">
                    <button type="button" className="btn text-danger" data-bs-dismiss="modal">Close</button>
                  </div>
                  <div className="col text-end">
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={handleSupportSubmit}
                    >
                      Confirm
                    </button>
                  </div>
              </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default SettingsPage;
